import React, { Component, Fragment } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Header from './components/Header';
import Footer from './components/Footer';
import ReportOverlay from './components/ReportOverlay';
import Modal from './../../components/Modal';
import AwaitingScoresAlert from './../../components/AwaitingScoresAlert/AwaitingScoresAlert';
import ReportElementIdentifiers from './constants/ReportElementIdentifiers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DownloadPdfButton from './../../components/DownloadPdfButton/DownloadPdfButton';
import moment from 'moment';
import Utils from './../../utils';
import PropTypes from 'prop-types';
import style from './style/report.scss'; // eslint-disable-line

class Report extends Component {
    constructor(props) {
        super(props);

        /* presentational state goes here */
        this.state = {
            fetchingReport: false,
            gridLayout: [],
            reportOverlayVisible: !!this.props.reportSectionHandle,
            stickyHeader: false,
            pollingScores: this.props.pollingScores || false
        };

        this.handleScroll = this.handleScroll.bind(this);
        this.getDownloadReportButton = this.getDownloadReportButton.bind(this);
        this.handleGetReport = this.handleGetReport.bind(this);
    }

    componentDidUpdate(prevProps) {
        const REPORT_MAIN = document.querySelector(`#${ReportElementIdentifiers.REPORT_MAIN}`) || null;
        const REPORT_DETAIL = document.querySelector(`#${ReportElementIdentifiers.REPORT_DETAIL}`) || null;

        if (REPORT_MAIN && REPORT_MAIN.getAttribute('listener') !== 'true') {
            REPORT_MAIN.addEventListener('scroll', this.handleScroll);
        }
        if (REPORT_DETAIL && REPORT_DETAIL.getAttribute('listener') !== 'true') {
            REPORT_DETAIL.addEventListener('scroll', this.handleScroll);
        }

        // if gridOverlay is changed, something like a detail page is probably requested, so update local state
        if (prevProps.reportOverlayVisible !== this.props.reportOverlayVisible) {
            this.setState({
                reportOverlayVisible: this.props.reportOverlayVisible
            });
        }
        if (prevProps.pollingScores !== this.props.pollingScores) {
            this.setState({
                pollingScores: this.props.pollingScores
            });
        }
    }

    /**
     * handleScroll
     *
     * keeps track of whether the header should have the sticky flag set to make it less tall
     *
     * @returns {void}
     */
    handleScroll() {
        const pageOpen = this.state.reportOverlayVisible ? `#${ReportElementIdentifiers.REPORT_DETAIL}` : `#${ReportElementIdentifiers.REPORT_MAIN}`;

        if (this.state.stickyHeader !== (document.querySelector(pageOpen).scrollTop > this.props.stickyHeaderScrollThreshold)) {
            this.setState({ stickyHeader: document.querySelector(pageOpen).scrollTop > this.props.stickyHeaderScrollThreshold });
            if (this.props.pageScrolled) {
                this.props.pageScrolled(document.querySelector(pageOpen).scrollTop > this.props.stickyHeaderScrollThreshold);
            }
        }
    }

    /**
     * handleGetReport
     * @returns {void}
     */
    handleGetReport() {
        this.setState({
            fetchingReport: true
        });

        this.props.getPdfReport().then(() => {
            this.setState({
                fetchingReport: false
            });
        }).catch(() => {
            this.setState({
                fetchingReport: false
            });
        });
    }

    getDownloadReportButton() {
        const { downloadButtonLabel, pollingScores, product, i18nParticipation, getPdfReport, allowedToWriteReport } = this.props;

        // do not allow downloading a report that a consultant can write to
        if (allowedToWriteReport) {
            return null;
        }

        /* this enables a call to report-service to download the report as pdf */
        if (!pollingScores && getPdfReport && product) {
            return <div className={ style.print_button_container }>
                <DownloadPdfButton
                    buttonLabel={ downloadButtonLabel || i18nParticipation.report_download_pdf_label }
                    loading={this.state.fetchingReport}
                    getPdfReport={ () => {
                        this.handleGetReport();
                    } }
                />
            </div>;
        }

        return null;
    }

    render() {
        const {
            reportData,
            i18nParticipation,
            reportSectionHandle,
            reportOverlayContent,
            children,
            defaultLanguage,
            classes,
            branding,
            product,
            permissionBarFixed,
            reportName,
            LoaderComponent
        } = this.props;

        const { reportOverlayVisible, stickyHeader, pollingScores } = this.state;

        const { organisation, participant, completedOn } = reportData;
        const { appointmentDate, firstName, infix, lastName } = participant;

        let detailPageTitle = i18nParticipation.report_page_title;

        if (reportSectionHandle) {
            detailPageTitle = i18nParticipation[`report_${Utils.camelCaseToSnakeCase(reportSectionHandle)}`] || reportSectionHandle;
        }

        const projectName = reportData?.project?.projectDisplayName || reportData?.project?.projectName || '';

        const reportHeader = reportOverlayVisible
            ? <Header sticky={ stickyHeader } classes={ classes } reportOverlayVisible >
                <div className={ `${style.header_contents} ${stickyHeader ? style.header_contents__sticky : ''}` }>

                    <div className={ `${style.header_contents_text_container} ${reportOverlayVisible ? style.header_contents_text_container__detail : ''}` }>
                        <ul className={ style.header_path }
                            onClick={ () => {
                                this.props.closeGridOverlay();
                            } }
                        >
                            <li>
                                <FontAwesomeIcon icon={ 'arrow-left' } />
                                { i18nParticipation.report_report_overview } { reportData.participant.firstName }
                                { reportData.participant.infix ? ` ${reportData.participant.infix} ` : ' ' }
                                { reportData.participant.lastName }
                                <span>/</span>
                                <strong>{ detailPageTitle }</strong>
                            </li>
                        </ul>


                        <ul className={ style.titleTableOnDetailPage }>
                            <li><h2>{ detailPageTitle }</h2></li>
                        </ul>

                    </div>
                    <div className={style.closeButton} onClick={() => this.props.closeGridOverlay()}>
                        <FontAwesomeIcon icon={ 'times' } />
                    </div>
                </div>

            </Header>
            : <Fragment>
                { pollingScores && (
                    <AwaitingScoresAlert>
                        <CircularProgress
                            size={ 12 }
                            color={ 'primary' }
                            classes={{ root: style.awaitingScoresAlertSpinner }}
                        />{ i18nParticipation.report_scores_are_being_calculated }
                    </AwaitingScoresAlert>
                ) }
                <Header sticky={ stickyHeader } alertOnTop={ pollingScores } classes={ classes } permissionBarFixed={ permissionBarFixed }>
                    <div className={ `${style.header_contents} ${stickyHeader ? style.header_contents__sticky : ''} ${permissionBarFixed ? style.permission_bar_fixed : ''}` }>

                        <div className={ style.company_logo_container }>
                            <div>
                                <img alt="logo" src={ organisation.logo || branding.logo } />
                            </div>
                        </div>

                        <div className={ style.header_contents_text_container }>
                            <ul>
                                <li>
                                    <h1>{ reportName || i18nParticipation[`report_${product.translationKey}`] }</h1>
                                </li>
                                <li>
                                    <h2>
                                        { firstName }
                                        { infix ? ` ${infix} ` : ' '}
                                        { lastName }
                                    </h2>
                                </li>
                            </ul>
                            <ul className={ style.infoTable }>
                                <li>
                                    <span>{ i18nParticipation.report_organisation }</span>
                                    <div className={ style.multiline_ellipsis }>
                                        { organisation.parentOrganisation || organisation.organisationName }
                                    </div>
                                </li>
                                <li>
                                    <span>{ i18nParticipation.report_function }</span>
                                    <div className={ style.multiline_ellipsis }>{ projectName }</div>
                                </li>
                                {!!appointmentDate && (
                                    <li>
                                        <span>{i18nParticipation.report_date}</span>
                                        {moment(appointmentDate).format('YYYY-MM-DD')}
                                    </li>
                                )}
                                {!!completedOn && (
                                    <li>
                                        <span>{i18nParticipation.report_completed_date}</span>
                                        <div className={ style.multiline_ellipsis }>{moment(completedOn).format('YYYY-MM-DD')}</div>
                                    </li>
                                )}
                            </ul>
                        </div>

                        { this.getDownloadReportButton() }

                    </div>
                </Header>
            </Fragment>;

        const footer = <Footer
            i18nParticipation={ i18nParticipation }
            showReportOverlay={ this.props.showReportOverlay }
            branding={ branding.footer || {} }
            classes={ { root: classes.footer || '' } }
        />;

        return (
            <Fragment>
                { !reportData.loaded
                    ? <LoaderComponent relative />
                    : <section>

                        <ReportOverlay
                            i18nParticipation={ i18nParticipation }
                            reportOverlayVisible={ reportOverlayVisible }
                            reportSectionHandle={ reportSectionHandle }
                            reportData={ reportData }
                            content={ reportOverlayContent }
                            participant={ reportData.participant }
                            defaultLanguage={ defaultLanguage }
                            product={ product }
                            classes= {{
                                foldOut: classes.foldOut
                            }}
                            LoaderComponent={LoaderComponent}
                        >{ footer }</ReportOverlay>

                        <div className={ `${style.modal_container} hidden` } id="modal_competency_profile_extended">
                            <Modal
                                classes={{
                                    modal: style.competencyProfileExtendedModal,
                                    modalBackground: style.competencyProfileExtendedModalBackground
                                }}
                                closeModal={ () => {
                                    this.props.closeCompetencyExtendedModal('modal_competency_profile_extended');
                                } }
                            >
                                { this.props.modalContent }
                            </Modal>
                        </div>

                        {/* container for report derailment modals */}
                        <div id="report_modal"/>

                        { reportHeader }

                        <main id="report_main" className={ `${style.reportData} ${stickyHeader ? style.reportData__with_sticky_header : ''} ${pollingScores ? style.reportData__with_alert_on_top : ''} ${permissionBarFixed ? style.permissionBarPositionFixed : ''}` }>
                            { children }
                            { footer }
                        </main>
                    </section>
                }
            </Fragment>
        );
    }
}

export default Report;

Report.propTypes = {
    classes: PropTypes.shape({
        header: PropTypes.string,
        headerSticky: PropTypes.string
    })
};

Report.defaultProps = {
    classes: {
        header: '',
        headerSticky: ''
    }
};
